/**
 * Meeting Toast Notification Types
 *
 * Types for the meeting toast notification system that alerts users
 * when meetings are starting (scheduled or impromptu).
 */
import { z } from 'zod';
// =============================================================================
// Constants
// =============================================================================
/**
 * Types of meeting toast notifications
 */
export const MeetingToastType = {
    /** Reminder notification X minutes before meeting */
    REMINDER: 'reminder',
    /** Meeting time has arrived notification */
    STARTED: 'started',
    /** Detected ad-hoc meeting join (Zoom, Teams, Meet, Webex) */
    IMPROMPTU: 'impromptu',
    /** Externally detected meeting requiring user confirmation */
    DETECTED: 'detected',
};
/**
 * Meeting platforms that can be detected
 */
export const MeetingPlatform = {
    ZOOM: 'zoom',
    GOOGLE_MEET: 'google_meet',
    TEAMS: 'teams',
};
// Display metadata for UI. Record<MeetingPlatformValue, ...> so a new platform
// is a compile error until it gets a label here.
export const MeetingPlatformMetadata = {
    zoom: { label: 'Zoom' },
    google_meet: { label: 'Google Meet' },
    teams: { label: 'Teams' },
};
/** Native toast window sizes. Keep the renderer gallery and main process in sync. */
export const MEETING_TOAST_SURFACE_SIZES = {
    standard: { width: 420, height: 160 },
    // The compact meeting layout can show both the Toast summary and meeting
    // description. Keep a small bottom buffer for the card shadow.
    meeting: { width: 420, height: 384 },
    // Detected meetings use the same interactive meeting-card surface.
    detected: { width: 420, height: 384 },
};
// =============================================================================
// Zod Schemas
// =============================================================================
/**
 * Attendee information for display in toast
 */
export const MeetingToastAttendeeSchema = z.object({
    /** Attendee email address */
    email: z.string().email(),
    /** Display name */
    name: z.string().nullable(),
    /** Avatar URL (S3 or external) */
    avatarUrl: z.string().nullable(),
    /** Job title (from research or contact) */
    title: z.string().nullable(),
    /** Company name */
    company: z.string().nullable(),
    /** Whether AI research is available for this attendee */
    hasResearch: z.boolean(),
    /** Whether this attendee is the organizer */
    isOrganizer: z.boolean().optional(),
});
/**
 * Main toast payload sent to renderer
 */
export const MeetingToastPayloadSchema = z.object({
    /** Unique toast ID */
    id: z.string().uuid(),
    /** Type of notification */
    type: z.enum(Object.values(MeetingToastType)),
    /** Associated calendar event ID (null for impromptu) */
    calendarEventId: z.string().uuid().nullable(),
    /** Meeting title */
    title: z.string(),
    /** Meeting description (truncated for display) */
    description: z.string().nullable(),
    /** Meeting start time (ISO 8601) */
    startTime: z.string(),
    /** Minutes until meeting starts (null for started/impromptu) */
    minutesUntilStart: z.number().nullable(),
    /** Meeting location (physical or virtual) */
    location: z.string().nullable(),
    /** Meeting join URL */
    meetingLink: z.string().nullable(),
    /** Detected meeting platform */
    meetingPlatform: z.enum(Object.values(MeetingPlatform)).nullable(),
    /** Associated client ID (if linked) */
    clientId: z.string().uuid().nullable(),
    /** Client name for display */
    clientName: z.string().nullable(),
    /** Client logo URL */
    clientLogoUrl: z.string().nullable(),
    /** Deal ID (if associated) */
    dealId: z.string().uuid().nullable(),
    /** Deal name for display */
    dealName: z.string().nullable(),
    /** Deal stage (if associated) */
    dealStage: z.string().nullable(),
    /** Attendees (max 15 for display) */
    attendees: z.array(MeetingToastAttendeeSchema).max(15),
    /** Toast creation timestamp */
    createdAt: z.string(),
    /** Detected meeting data (only present for type 'detected') */
    detectedData: z.object({
        detectedTitle: z.string(),
        platform: z.string(),
        suggestedClientId: z.string().nullable(),
        suggestedClientName: z.string().nullable(),
        suggestedDealId: z.string().nullable(),
        suggestedDealName: z.string().nullable(),
        matchedMeetingId: z.string().nullable(),
        matchedMeetingTitle: z.string().nullable(),
    }).optional(),
});
/**
 * User settings for meeting notifications
 */
export const MeetingToastSettingsSchema = z.object({
    /** Whether meeting notifications are enabled */
    enabled: z.boolean().default(true),
    /** Whether reminder notifications are enabled */
    showReminderNotification: z.boolean().default(true),
    /** Minutes before meeting to show reminder (1, 2, 5, 10, 15, 30) */
    reminderMinutes: z.number().min(1).max(60).default(10),
    /** Whether to show notifications for detected ad-hoc meeting joins */
    detectImpromptu: z.boolean().default(true),
    /** Show "Meeting Started" notification when time arrives */
    showStartedNotification: z.boolean().default(true),
});
/**
 * Quick research view for attendee popover
 */
export const AttendeeQuickResearchSchema = z.object({
    /** Attendee email */
    email: z.string().email(),
    /** Full name */
    name: z.string().nullable(),
    /** Current job title */
    currentTitle: z.string().nullable(),
    /** Current company */
    currentCompany: z.string().nullable(),
    /** LinkedIn profile URL */
    linkedinUrl: z.string().nullable(),
    /** Brief LinkedIn summary */
    linkedinSummary: z.string().nullable(),
    /** Compact summary generated for the meeting Toast */
    shortSummary: z.string().nullable().optional(),
    /** Top 3 talking points for conversation */
    topTalkingPoints: z.array(z.string()).max(3),
    /** Loading state */
    isLoading: z.boolean(),
    /** Error message if research failed */
    error: z.string().nullable(),
});
/**
 * Meeting identifier extracted from window title (e.g., Google Meet code, Zoom meeting ID)
 */
export const MeetingIdentifierSchema = z.object({
    /** Identifier type: 'meet_code' (Google Meet), 'zoom_id' (Zoom meeting ID) */
    type: z.enum(['meet_code', 'zoom_id']),
    /** The extracted value (e.g., 'abc-defg-hij' or '1234567890') */
    value: z.string(),
});
/**
 * Detected meeting from window title monitoring
 */
export const DetectedMeetingSchema = z.object({
    /** Meeting platform detected */
    platform: z.enum(['zoom', 'google_meet', 'teams', 'webex']),
    /** Window title (may contain meeting name) */
    title: z.string(),
    /** Process name that owns the window */
    processName: z.string(),
    /** Raw window title for debugging */
    rawWindowTitle: z.string(),
    /** Extracted meeting identifier for DB matching (null if not extractable) */
    meetingIdentifier: MeetingIdentifierSchema.nullable(),
    /** Detection timestamp */
    detectedAt: z.string(),
});
// =============================================================================
// Settings Keys (for SQLite storage)
// =============================================================================
export const MEETING_TOAST_SETTINGS_KEYS = {
    ENABLED: 'meeting_notifications_enabled',
    SHOW_REMINDER: 'meeting_show_reminder_notification',
    REMINDER_MINUTES: 'meeting_reminder_minutes',
    DETECT_IMPROMPTU: 'meeting_detect_impromptu',
    SHOW_STARTED: 'meeting_show_started_notification',
};
